import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { ArrowLeftIcon, TrashIcon, EyeIcon, EyeSlashIcon, PencilIcon, PhotoIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';

interface ShefuPuzzle {
  id: number;
  title: string;
  question_text: string | null;
  question_image_url: string | null;
  answer_text: string | null;
  answer_image_url: string | null;
  created_at: string;
  author: string;
  user_id: number;
}

interface ShefuAnswer {
  id: number;
  answer_text: string | null;
  answer_image_url: string | null;
  created_at: string;
  updated_at: string;
  user_id: number;
  author: string;
}

export default function ShefuDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const [puzzle, setPuzzle] = useState<ShefuPuzzle | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAnswer, setShowAnswer] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // User answers state
  const [answers, setAnswers] = useState<ShefuAnswer[]>([]);
  const [loadingAnswers, setLoadingAnswers] = useState(false);
  const [answerText, setAnswerText] = useState('卦象：\n解卦：\n断卦：\n');
  const [answerImageUrl, setAnswerImageUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [expandedAnswers, setExpandedAnswers] = useState<Set<number>>(new Set());
  const [showAllAnswers, setShowAllAnswers] = useState(false);

  useEffect(() => {
    fetchPuzzleDetail();
    fetchAnswers();
  }, [id]);

  const fetchPuzzleDetail = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/shefu?action=detail&id=${id}`);
      const data = await response.json();

      if (data.success) {
        setPuzzle(data.puzzle);
      } else {
        console.error('Failed to fetch puzzle');
      }
    } catch (error) {
      console.error('Failed to fetch puzzle detail', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnswers = async () => {
    setLoadingAnswers(true);
    try {
      const response = await fetch(`/api/shefu?action=get-answers&id=${id}`);
      const data = await response.json();

      if (data.success) {
        setAnswers(data.answers);

        // If current user has an answer, pre-fill the form
        if (user) {
          const userAnswer = data.answers.find((a: ShefuAnswer) => a.user_id === user.id);
          if (userAnswer) {
            setAnswerText(userAnswer.answer_text || '卦象：\n解卦：\n断卦：\n');
            setAnswerImageUrl(userAnswer.answer_image_url || '');
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch answers', error);
    } finally {
      setLoadingAnswers(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('图片大小不能超过 5MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('只能上传图片文件');
      return;
    }

    setUploadingImage(true);
    try {
      const token = localStorage.getItem('auth-storage');
      const authData = token ? JSON.parse(token) : null;

      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64String = reader.result as string;

          const response = await fetch('/api/upload/image', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authData?.state?.token}`
            },
            body: JSON.stringify({
              image: base64String,
              filename: file.name
            })
          });

          const data = await response.json();

          if (data.success) {
            setAnswerImageUrl(data.imageUrl);
          } else {
            alert(data.message || '上传失败');
          }
        } catch (error) {
          console.error('Failed to upload image', error);
          alert('上传失败，请稍后重试');
        } finally {
          setUploadingImage(false);
        }
      };

      reader.onerror = () => {
        alert('读取图片失败');
        setUploadingImage(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Failed to process image', error);
      alert('处理图片失败，请稍后重试');
      setUploadingImage(false);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!user) {
      alert('请先登录');
      return;
    }

    // Check if user has entered any custom content (not just the template)
    const defaultTemplate = '卦象：\n解卦：\n断卦：\n';
    const trimmedText = answerText.trim();
    const hasCustomText = trimmedText && trimmedText !== defaultTemplate.trim();

    if (!hasCustomText && !answerImageUrl) {
      alert('请至少填写文字或上传图片');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('auth-storage');
      const authData = token ? JSON.parse(token) : null;

      const response = await fetch(`/api/shefu?action=submit-answer&id=${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authData?.state?.token}`
        },
        body: JSON.stringify({
          answerText: hasCustomText ? trimmedText : null,
          answerImageUrl: answerImageUrl || null
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(data.message);
        fetchAnswers(); // Refresh answers list
      } else {
        alert(data.message || '提交失败');
      }
    } catch (error) {
      console.error('Failed to submit answer', error);
      alert('提交失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleAnswerExpanded = (answerId: number) => {
    const newExpanded = new Set(expandedAnswers);
    if (newExpanded.has(answerId)) {
      newExpanded.delete(answerId);
    } else {
      newExpanded.add(answerId);
    }
    setExpandedAnswers(newExpanded);
  };

  const toggleAllAnswers = () => {
    if (showAllAnswers) {
      // Hide all
      setShowAllAnswers(false);
      setExpandedAnswers(new Set());
    } else {
      // Show all
      setShowAllAnswers(true);
      setExpandedAnswers(new Set(answers.map(a => a.id)));
    }
  };

  const handleDeleteAnswer = async (answerId: number) => {
    if (!window.confirm('确定要删除这条回答吗？')) {
      return;
    }

    try {
      const token = localStorage.getItem('auth-storage');
      const authData = token ? JSON.parse(token) : null;

      const response = await fetch(`/api/shefu?action=delete-answer&id=${answerId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authData?.state?.token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        alert('删除成功');
        fetchAnswers(); // Refresh answers list
        // Reset form if deleted own answer
        const deletedAnswer = answers.find(a => a.id === answerId);
        if (deletedAnswer && user && deletedAnswer.user_id === user.id) {
          setAnswerText('卦象：\n解卦：\n断卦：\n');
          setAnswerImageUrl('');
        }
        // Remove from expanded set
        const newExpanded = new Set(expandedAnswers);
        newExpanded.delete(answerId);
        setExpandedAnswers(newExpanded);
      } else {
        alert(data.message || '删除失败');
      }
    } catch (error) {
      console.error('Failed to delete answer', error);
      alert('删除失败，请稍后重试');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('确定要删除这道射覆题目吗？')) {
      return;
    }

    setDeleting(true);
    try {
      const token = localStorage.getItem('auth-storage');
      const authData = token ? JSON.parse(token) : null;

      const response = await fetch(`/api/shefu?action=delete&id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authData?.state?.token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        navigate('/academy/shefu');
      } else {
        console.error('Delete failed:', data, 'Status:', response.status);
        alert(data.message || `删除失败 (${response.status})`);
      }
    } catch (error) {
      console.error('Failed to delete puzzle', error);
      alert(`删除失败：${error instanceof Error ? error.message : '请稍后重试'}`);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-6 px-4">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <p className="mt-2 text-gray-500">加载中...</p>
        </div>
      </div>
    );
  }

  if (!puzzle) {
    return (
      <div className="max-w-4xl mx-auto py-6 px-4">
        <div className="text-center py-12">
          <p className="text-gray-500">题目不存在</p>
          <button
            onClick={() => navigate('/academy/shefu')}
            className="mt-4 text-red-600 hover:text-red-700"
          >
            返回列表
          </button>
        </div>
      </div>
    );
  }

  const isAuthor = user && user.id === puzzle.user_id;
  const isSuperAdmin = user && user.role === 'superadmin';
  const canEdit = isAuthor || isSuperAdmin;

  return (
    <div className="max-w-4xl mx-auto py-6 px-4">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/academy/shefu')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <span>返回列表</span>
        </button>

        {canEdit && (
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(`/academy/shefu/${id}/edit`)}
              className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <PencilIcon className="w-5 h-5" />
              <span>编辑</span>
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex items-center space-x-2 px-4 py-2 text-red-600 border border-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              <TrashIcon className="w-5 h-5" />
              <span>{deleting ? '删除中...' : '删除'}</span>
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{puzzle.title}</h1>

        <div className="flex items-center justify-between text-sm text-gray-500 mb-6">
          <span>作者：{puzzle.author}</span>
          <span>{dayjs(puzzle.created_at).format('YYYY年MM月DD日 HH:mm')}</span>
        </div>

        <div className="border-t pt-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">题目</h2>

          {puzzle.question_image_url && (
            <img
              src={puzzle.question_image_url}
              alt={puzzle.title}
              className="w-full max-h-96 object-contain rounded-lg mb-4"
            />
          )}

          {puzzle.question_text && (
            <p className="text-gray-700 whitespace-pre-wrap">{puzzle.question_text}</p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">答案</h2>
          <button
            onClick={() => setShowAnswer(!showAnswer)}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            {showAnswer ? (
              <>
                <EyeSlashIcon className="w-5 h-5" />
                <span>隐藏答案</span>
              </>
            ) : (
              <>
                <EyeIcon className="w-5 h-5" />
                <span>显示答案</span>
              </>
            )}
          </button>
        </div>

        {showAnswer ? (
          <div>
            {puzzle.answer_image_url && (
              <img
                src={puzzle.answer_image_url}
                alt="答案"
                className="w-full max-h-96 object-contain rounded-lg mb-4"
              />
            )}

            {puzzle.answer_text && (
              <p className="text-gray-700 whitespace-pre-wrap">{puzzle.answer_text}</p>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            点击"显示答案"按钮查看答案
          </div>
        )}
      </div>

      {/* User Answer Submission Form */}
      {user && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">我的回答</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                文字回答
              </label>
              <textarea
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                rows={8}
                placeholder="卦象：&#10;解卦：&#10;断卦：&#10;"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                图片 (可选)
              </label>
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer">
                  <PhotoIcon className="w-5 h-5" />
                  <span>{uploadingImage ? '上传中...' : '上传图片'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploadingImage}
                  />
                </label>
                {answerImageUrl && (
                  <button
                    onClick={() => setAnswerImageUrl('')}
                    className="text-red-600 hover:text-red-700 text-sm"
                  >
                    移除图片
                  </button>
                )}
              </div>
              {answerImageUrl && (
                <img
                  src={answerImageUrl}
                  alt="预览"
                  className="mt-4 w-full max-h-64 object-contain rounded-lg border"
                />
              )}
            </div>

            <button
              onClick={handleSubmitAnswer}
              disabled={submitting || uploadingImage}
              className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? '提交中...' : '提交回答'}
            </button>
          </div>
        </div>
      )}

      {/* User Answers List */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">
            用户回答 ({answers.length})
          </h2>
          {answers.length > 0 && (
            <button
              onClick={toggleAllAnswers}
              className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
            >
              {showAllAnswers ? '隐藏全部' : '展开全部'}
            </button>
          )}
        </div>

        {loadingAnswers ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
            <p className="mt-2 text-gray-500 text-sm">加载中...</p>
          </div>
        ) : answers.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            暂无回答
          </div>
        ) : (
          <div className="space-y-6">
            {answers.map((answer) => {
              const isOwnAnswer = user && answer.user_id === user.id;
              const isSuperAdmin = user && user.role === 'superadmin';
              const canDeleteAnswer = isOwnAnswer || isSuperAdmin;
              const isExpanded = expandedAnswers.has(answer.id);

              return (
                <div key={answer.id} className="border-b pb-6 last:border-b-0 last:pb-0">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2 flex-1">
                      <button
                        onClick={() => toggleAnswerExpanded(answer.id)}
                        className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                      >
                        <svg
                          className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                      <span className="font-medium text-gray-900">{answer.author}</span>
                      {isOwnAnswer && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                          我
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-gray-500">
                        {dayjs(answer.created_at).format('YYYY-MM-DD HH:mm')}
                      </span>
                      {canDeleteAnswer && (
                        <button
                          onClick={() => handleDeleteAnswer(answer.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="ml-8 pl-4 border-l-2 border-gray-200">
                      {answer.answer_image_url && (
                        <img
                          src={answer.answer_image_url}
                          alt="回答"
                          className="w-full max-h-96 object-contain rounded-lg mb-3"
                        />
                      )}

                      {answer.answer_text && (
                        <p className="text-gray-700 whitespace-pre-wrap">{answer.answer_text}</p>
                      )}

                      {answer.updated_at !== answer.created_at && (
                        <p className="text-xs text-gray-400 mt-2">
                          已编辑于 {dayjs(answer.updated_at).format('YYYY-MM-DD HH:mm')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
